import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import WelcomeHeader from './components/WelcomeHeader';
import MetricCard from './components/MetricCard';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import RegionalDistribution from './components/RegionalDistribution';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import Icon from '../../components/AppIcon';


const MemberDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  // Mock dashboard data
  const mockDashboardData = {
    totalMembers: 4365,
    newMembersThisMonth: 127,
    activeMembers: 4201,
    pendingApprovals: 23,
    memberGrowth: {
      total: '+8.2%',
      type: 'increase'
    },
    monthlyGrowth: {
      total: '+15.3%',
      type: 'increase'
    },
    activeGrowth: {
      total: '+3.1%',
      type: 'increase'
    },
    pendingGrowth: {
      total: '-12.5%',
      type: 'decrease'
    }
  };

  useEffect(() => {
    // Simulate loading dashboard data
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        setDashboardData(mockDashboardData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleQuickNavigation = (path) => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <LoadingOverlay 
          isVisible={true} 
          message="Carregando dados do painel..." 
          type="processing"
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Welcome Header */}
          <WelcomeHeader />

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Total de Membros"
              value={dashboardData?.totalMembers?.toLocaleString() || '0'}
              change={dashboardData?.memberGrowth?.total}
              changeType={dashboardData?.memberGrowth?.type}
              icon="Users"
              color="primary"
            />
            <MetricCard
              title="Membros novos no mês"
              value={dashboardData?.newMembersThisMonth?.toLocaleString() || '0'}
              change={dashboardData?.monthlyGrowth?.total}
              changeType={dashboardData?.monthlyGrowth?.type}
              icon="UserPlus"
              color="success"
            />
            <MetricCard
              title="Membros ativos"
              value={dashboardData?.activeMembers?.toLocaleString() || '0'}
              change={dashboardData?.activeGrowth?.total}
              changeType={dashboardData?.activeGrowth?.type}
              icon="UserCheck"
              color="primary"
            />
            <MetricCard
              title="Aprovações pendentes"
              value={dashboardData?.pendingApprovals?.toLocaleString() || '0'}
              change={dashboardData?.pendingGrowth?.total}
              changeType={dashboardData?.pendingGrowth?.type}
              icon="Clock"
              color="warning"
            />
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <QuickActions />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Recent Activity */}
            <div className="lg:col-span-1">
              <RecentActivity />
            </div>

            {/* Regional Distribution */}
            <div className="lg:col-span-2">
              <RegionalDistribution />
            </div>
          </div>

          {/* Additional Actions */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Ferramentas de gestão</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => handleQuickNavigation('/member-list')}
                className="flex items-center space-x-3 p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors duration-200 contextual-fade"
              >
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="List" size={20} className="text-primary-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Ver todos os membros</p>
                  <p className="text-xs text-muted-foreground">Navegar e gerenciar</p>
                </div>
              </button>

              <button
                onClick={() => handleQuickNavigation('/member-registration')}
                className="flex items-center space-x-3 p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors duration-200 contextual-fade"
              >
                <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                  <Icon name="UserPlus" size={20} className="text-success-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Adicionar membro</p>
                  <p className="text-xs text-muted-foreground">Cadastrar novo</p>
                </div>
              </button>

              <button
                onClick={() => handleQuickNavigation('/member-import')}
                className="flex items-center space-x-3 p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors duration-200 contextual-fade"
              >
                <div className="w-10 h-10 bg-warning rounded-lg flex items-center justify-center">
                  <Icon name="Upload" size={20} className="text-warning-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Importação em massa</p>
                  <p className="text-xs text-muted-foreground">Upload CSV/Excel</p>
                </div>
              </button>

              <button
                onClick={() => console.log('Analytics clicked')}
                className="flex items-center space-x-3 p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors duration-200 contextual-fade"
              >
                <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                  <Icon name="BarChart3" size={20} className="text-accent-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Analytics</p>
                  <p className="text-xs text-muted-foreground">Relatórios detalhados</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MemberDashboard;